import { Location } from '@angular/common';
import { AfterViewInit, Directive, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AccountService, ProfileService, AuthService } from '@app/core/services';
import { AbstractPane } from '@app/library/panes/models/abstract-pane';
import { RedPointsService } from '@core/services/red-points.service';
import { Store } from '@ngrx/store';
import { setRedPoints } from '@store/actions/profile.actions';
import { ERedPointsEvents, IRedPointsEvents } from '@store/models';
import { getMe, getRedPoints } from '@store/reducers/profile.reducer';
import { User, UserService, UserStatusEnum, RedPointTypeEnum } from 'lingo2-models';
import { takeUntil } from 'rxjs/operators';

/*
 */
export interface IBasePaneTab {
  code?: string;
  title?: string;
  stub?: any;
  stub_guest?: string;
  [key: string]: any;
}

/*
 */
@Directive()
export abstract class BasePaneComponent<TTab extends IBasePaneTab>
  extends AbstractPane
  implements OnInit, AfterViewInit
{
  protected readonly store = inject(Store);
  protected readonly authService = inject(AuthService);
  protected readonly redPointsService = inject(RedPointsService);
  protected readonly profileService = inject(ProfileService);
  protected readonly location = inject(Location);
  protected readonly activatedRoute = inject(ActivatedRoute);

  /*
   */
  public editService: UserService;
  public newService: UserService;

  protected _me: User;
  public isTeacher: boolean;
  protected _redPointsEvents: IRedPointsEvents;
  protected _profile: any;

  public tabs: TTab[] = [];
  public activeTab: TTab;

  public newEntity: any;
  public editEntity: any;
  public openedEntity: any;
  public entityInModal = false;

  protected originalUrl: string;

  /* NB! Implement in child!
   */
  protected abstract getEntityRoute(entity: any): string;
  protected abstract setupFilter(tab: TTab): void;
  protected abstract redPointEvent(tab: TTab): ERedPointsEvents;
  protected abstract createNewEntity(): any;

  /*
   */
  public get isGuest(): boolean {
    return this._me?.status === UserStatusEnum.guest;
  }

  public get isAuthenticated(): boolean {
    return !!this._me && this._me.status !== UserStatusEnum.guest;
  }

  public get isAsIfTeacher(): boolean {
    return AccountService.isAsIfTeacher(this._me);
  }

  protected get shouldLoadProfile(): boolean {
    return true;
  }

  /*
   */
  ngOnInit(): void {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((me) => {
        this._me = me;
        this.isTeacher = AccountService.isAsIfTeacher(me);
        this.detectChanges();
      });

    this.store
      .select(getRedPoints)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((events) => {
        this._redPointsEvents = events;
        this.detectChanges();
      });
  }

  /*
   */
  ngAfterViewInit(): void {
    this.initTab();
  }

  /*
   */
  public onCreate(): void {
    this.newEntity = this.createNewEntity();
    this.detectChanges();
  }

  /*
   */
  protected initTab(): void {
    const tabParam = this.activatedRoute?.snapshot?.queryParams?.['tab'];
    const defaultTab = this.tabs.find((t) => t.code === tabParam) || this.tabs[0];
    this.activeTab = null;
    this.activateTab(defaultTab, true);
  }

  /*
   */
  protected activateDefaultTab(): void {
    if (!this.activeTab && this.tabs.length > 0) {
      this.onSelectTab(this.tabs[0]);
    }
  }

  public activateTab(tab: TTab, force = false): void {
    if (this.activeTab?.code !== tab.code || force) {
      this.onSelectTab(tab);
    }
  }

  public onSelectTab(tab: TTab): void {
    if (!tab) return;

    this.activeTab = tab;
    this.setupFilter(tab);
    this.resetPane();
    this.loadPane();
    this.removeRedPoint(this.redPointEvent(tab));
    this.detectChanges();
  }

  protected resetPane(): void {
    this._pane = {
      ...this._pane,
      loaded: false,
      contentItems: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0,
      },
    };
  }

  public onSelect(service: any) {
    this.editService = service;
    this.detectChanges();
  }

  public onCancel(): void {
    this.editService = null;
    this.newService = null;

    if (this.isBrowser && localStorage) {
      localStorage.setItem(
        'lastClosedSuggestAddCard',
        new Date(new Date().setDate(new Date().getDate() - 1)).toString(),
      );
      localStorage.setItem('userServiceCreated', 'true');
    }
  }

  protected override onContentLoaded(viaQuery?: boolean): void {
    this.isVisible = true;
    this.detectChanges();
  }

  protected removeRedPoint(event: ERedPointsEvents): void {
    if (!event || !this._redPointsEvents?.[event]) return;
    const redPoint = RedPointTypeEnum[event];
    this.redPointsService
      .removeRedPoint(redPoint)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.store.dispatch(
          setRedPoints({ points: { ...this._redPointsEvents, [RedPointTypeEnum[redPoint]]: false } }),
        );
      });
  }

  public onInViewportBottomChange(reached: { visible: boolean }) {
    if (reached.visible) {
      this.loadNextPage();
    }
  }

  public trackByFn(index: number): number {
    return index;
  }

  protected loadProfile(): void {
    if (this._me?.profile) {
      this._profile = this._me.profile;
      this.contextService.patchProfile(this._profile);
    } else {
      this.profileService
        .getProfile(this._me?.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (profile) => {
            this._profile = profile;
            this.contextService.patchProfile(this._profile);
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          },
        });
    }
  }

  public toggleEntityModal(entity: any): void {
    this.openedEntity = entity;
    this.entityInModal = !this.entityInModal;
    this.setAndResetUrl();
    this.detectChanges();
  }

  protected setAndResetUrl(): void {
    const url = this.getEntityRoute(this.openedEntity);
    const current = this.location?.path(false);

    if (this.entityInModal) {
      if (current !== url) {
        if (!this.originalUrl) this.originalUrl = current;
        this.location.replaceState(url);
      }
    } else {
      if (this.originalUrl) {
        this.location.replaceState(this.originalUrl);
        this.originalUrl = null;
      }
    }
  }

  public onCloseForm(e): void {
    if (e) {
      this.setTimeout(() => {
        const modTab = this.tabs.find((tab) => this.redPointEvent(tab) === ERedPointsEvents.my_user_service_moderation);
        if (modTab) this.activateTab(modTab, true);
      }, 1000);
    }
    this.onCancel();
  }
}
